import React, { useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface AgentProfileEditDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: 'overview' | 'professional' | 'contact';
  data: any;
  onSave: (updatedData: any) => void;
}

const AgentProfileEditDrawer: React.FC<AgentProfileEditDrawerProps> = ({
  open,
  onOpenChange,
  section,
  data,
  onSave
}) => {
  const [formData, setFormData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await onSave(formData);
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const renderOverviewFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="display_name">Display Name</Label>
        <Input
          id="display_name"
          value={formData.display_name || ''}
          onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bio">Bio</Label>
        <Textarea
          id="bio"
          value={formData.bio || ''}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location || ''}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
      </div>
    </>
  );

  const renderProfessionalFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="professional_name">Professional Name</Label>
        <Input
          id="professional_name"
          value={formData.agent?.professional_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, professional_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="agency_name">Agency Name</Label>
        <Input
          id="agency_name"
          value={formData.agent?.agency_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, agency_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="years_experience">Years of Experience</Label>
        <Input
          id="years_experience"
          type="number"
          value={formData.agent?.years_experience || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, years_experience: parseInt(e.target.value) || 0 }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="commission_rate">Commission Rate (%)</Label>
        <Input
          id="commission_rate"
          type="number"
          value={formData.agent?.commission_rate || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, commission_rate: parseFloat(e.target.value) || 0 }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="professional_bio">Professional Bio</Label>
        <Textarea
          id="professional_bio"
          value={formData.agent?.professional_bio || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, professional_bio: e.target.value }
          })}
          rows={3}
        />
      </div>
    </>
  );

  const renderContactFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="professional_email">Professional Email</Label>
        <Input
          id="professional_email"
          type="email"
          value={formData.agent?.professional_email || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, professional_email: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          value={formData.agent?.phone || ''}
          onChange={(e) => setFormData({
            ...formData,
            agent: { ...formData.agent, phone: e.target.value }
          })}
        />
      </div>
    </>
  );

  const getSectionTitle = () => {
    switch (section) {
      case 'overview': return 'Edit Overview';
      case 'professional': return 'Edit Professional Details';
      case 'contact': return 'Edit Contact Info';
      default: return 'Edit Profile';
    }
  };

  const renderFields = () => {
    switch (section) {
      case 'overview': return renderOverviewFields();
      case 'professional': return renderProfessionalFields();
      case 'contact': return renderContactFields();
      default: return null;
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <DrawerTitle>{getSectionTitle()}</DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="px-4 pb-4 space-y-4">
          {renderFields()}
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={isLoading} className="flex-1">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AgentProfileEditDrawer;